/**
 * Arche → Access (ADR-026): who may use this arche.
 *
 *   Anyone                      — public, no sign-in (not for arches that use AI)
 *   Anyone with a Fias account  — signed in
 *   Only people I choose        — restricted: its team plus the people,
 *                                 workspaces, Companies and guilds chosen here
 *
 * The same panel serves the arche's own page and Company → Owned arches; the
 * server decides who may change it (the Company, in a session with MFA, for a
 * company-owned arche).
 *
 * Choosing an option changes nothing until Save. A radio group moves its
 * selection with the arrow keys, so applying on change would let a keyboard
 * user open an internal arche to everyone without meaning to. Save then asks
 * first whenever the change widens who can use it or cannot be undone:
 * restricting states what it does and what it cannot undo (files in the
 * plugin's `public/` folder stay public, anything public may already have been
 * copied, content credentials issued before now keep their expiry); opening a
 * restricted arche up, or opening any arche to people who are not signed in,
 * says who can open it from then on.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { UserSearchCombobox } from '@/components/UserSearchCombobox';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type ArcheAudienceDto,
  type ArcheAudienceGrantDto,
  type ArcheAudienceKind,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  companyErrorCode,
  describeCompanyError,
  isRequestRefusal,
} from '@/auth/lib/company-errors';
import { ARCHE_AUDIENCE, ARCHE_AUDIENCE_ORDER } from '@/auth/lib/arche-governance-labels';
import { useMyCompanies } from '@/auth/hooks/useCompanies';
import {
  useArcheAudience,
  useFinishArcheRestriction,
  useGrantArcheAudience,
  useRevokeArcheAudienceGrant,
  useSetArcheAudience,
} from '@/auth/hooks/useArcheGovernance';

type GranteeType = ArcheAudienceGrantDto['granteeType'];

const GRANTEE_LABEL: Record<GranteeType, string> = {
  user: 'Person',
  workspace: 'Workspace',
  company: 'Company',
  guild: 'Guild',
};

/** Said when the viewer belongs to none of a kind — only their own can be added. */
const NONE_TO_OFFER: Record<Exclude<GranteeType, 'user'>, string> = {
  workspace: 'You are not a member of any shared workspace.',
  company: 'You have no role on any Company.',
  guild: 'You are not a member of any guild.',
};

/** Refusals the Company's admin can act on, rather than "not yours to manage". */
const COMPANY_GOVERNANCE_CODES = new Set([
  'COMPANY_GOVERNED',
  'COMPANY_GOVERNANCE_SESSION_ONLY',
  'MFA_REQUIRED_FOR_COMPANY',
  'MFA_REVALIDATION_REQUIRED',
]);

export function ArcheAccessPanel({ archeId }: { archeId: string }) {
  const audience = useArcheAudience(archeId);
  const setAudience = useSetArcheAudience(archeId);
  const finish = useFinishArcheRestriction(archeId);
  // The option chosen but not saved yet; null shows the saved one.
  const [draft, setDraft] = useState<ArcheAudienceKind | null>(null);
  // The change Save is asking about, while its dialog is open.
  const [confirming, setConfirming] = useState<ArcheAudienceKind | null>(null);
  // "Restrict to this Company": on a company-owned arche the owning Company
  // is granted in the same transaction as the restriction — the one-step
  // default for an internal tool. Checked by default; the dialog says so.
  const [grantCompany, setGrantCompany] = useState(true);

  if (audience.isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Access</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }
  // A Company-governance refusal is actionable — the admin's MFA lapsed, or the
  // request came the wrong way — so it is said; on Company → Owned arches the
  // panel is what the admin clicked for. Any other refusal ("not yours", not a
  // plugin) keeps the panel silent, as on the arche's own page. A failure to
  // load (a 5xx, the network) is said too, with a way to try again — the
  // panel must not simply vanish.
  if (audience.isError) {
    const code = companyErrorCode(audience.error);
    if (code && COMPANY_GOVERNANCE_CODES.has(code)) {
      return (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Access</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {describeCompanyError(audience.error)}
          </CardContent>
        </Card>
      );
    }
    if (isRequestRefusal(audience.error)) return null;
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Access</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="text-destructive">Could not load who can use this arche.</span>
          <Button size="sm" variant="outline" onClick={() => void audience.refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!audience.data) return null;
  const data = audience.data;
  const selected = draft ?? data.audience;

  function save(): void {
    if (selected === data.audience) return;
    if (asksFirst(data.audience, selected)) {
      setConfirming(selected);
      return;
    }
    void apply(selected);
  }

  async function apply(next: ArcheAudienceKind): Promise<void> {
    try {
      const grantOwningCompany = next === 'restricted' && !!data.owningCompany && grantCompany;
      const result = await setAudience.mutateAsync({ audience: next, grantOwningCompany });
      setDraft(null);
      const granted = result.grantedOwningCompany
        ? `Everyone at ${data.owningCompany?.name ?? 'the Company'} can use it.`
        : '';
      toast({
        title: 'Access updated',
        description: result.siteSnapshotRemovalPending
          ? [
              'Restricted.',
              granted,
              'Its public web pages are still being taken down — retry below if this persists.',
            ]
              .filter(Boolean)
              .join(' ')
          : granted || undefined,
      });
    } catch (err) {
      toast({
        title: 'Could not update access',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  const restrictDisabled = data.audience !== 'restricted' && !data.restrictionEnabled;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Access</CardTitle>
        <CardDescription>Who can open and use this arche.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <fieldset className="space-y-2" disabled={setAudience.isPending}>
          <legend className="sr-only">Who can use this arche</legend>
          {ARCHE_AUDIENCE_ORDER.map((value) => {
            const opt = ARCHE_AUDIENCE[value];
            const disabled = value === 'restricted' && restrictDisabled;
            return (
              <label key={value} className="flex items-start gap-3 text-sm">
                <input
                  type="radio"
                  name={`audience-${archeId}`}
                  className="mt-1 h-4 w-4"
                  value={value}
                  checked={selected === value}
                  disabled={disabled}
                  // Selecting only stages the choice; Save applies it.
                  onChange={() => setDraft(value === data.audience ? null : value)}
                />
                <span>
                  <span className="font-medium">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {disabled ? 'Restricting arches is not available right now.' : opt.hint}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
        {selected !== data.audience && (
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={setAudience.isPending} onClick={save}>
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={setAudience.isPending}
              onClick={() => setDraft(null)}
            >
              Cancel
            </Button>
            <span className="text-xs text-muted-foreground">Not saved yet.</span>
          </div>
        )}

        {data.siteSnapshotRemovalPending && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
            <span>
              <span className="font-medium">Finishing restriction…</span> This arche&apos;s public
              web pages are still being taken down.
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={finish.isPending}
              onClick={async () => {
                try {
                  const r = await finish.mutateAsync();
                  toast({
                    title: r.siteSnapshotRemovalPending
                      ? 'Still finishing — try again shortly'
                      : 'Done',
                  });
                } catch (err) {
                  toast({
                    title: 'Could not retry',
                    description: describeCompanyError(err),
                    variant: 'destructive',
                  });
                }
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {data.audience === 'restricted' && (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">People who can use it</h3>
            <p className="text-xs text-muted-foreground">
              Its team always can. A workspace or Company grant follows membership: people who join
              get access, people who leave lose it.
            </p>
            {data.grants.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nobody added yet.</p>
            ) : (
              <GrantList archeId={archeId} grants={data.grants} />
            )}
            <AddGranteeForm archeId={archeId} />
          </section>
        )}

        <p className="text-xs text-muted-foreground">
          Files in the plugin&apos;s <code>public/</code> folder are always public. Anything
          confidential belongs in a content pack.
        </p>
      </CardContent>

      <AlertDialog
        open={confirming === 'restricted'}
        onOpenChange={(o) => !o && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restrict this arche?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  From now on only its team and the people you add can open it. It leaves the store
                  and search, and its public web pages are taken down.
                </p>
                <p>
                  Anything that was public may already have been copied. Restricting protects what
                  happens from now on.
                </p>
                <p>
                  Files in the plugin&apos;s <code>public/</code> folder stay public to anyone who
                  has their link. Put anything confidential in a content pack — its access follows
                  this setting.
                </p>
                <p>
                  Content access already given out keeps its original expiry (up to 12 hours); from
                  now on it lasts 15 minutes. Someone already using the arche can finish what they
                  are doing; paid actions stop at their next step.
                </p>
                {data.owningCompany && (
                  <label className="flex items-start gap-2 rounded-md border p-2 text-foreground">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={grantCompany}
                      onChange={(e) => setGrantCompany(e.target.checked)}
                    />
                    <span>
                      Let everyone at{' '}
                      <span className="font-medium">
                        {data.owningCompany.name ?? 'the owning Company'}
                      </span>{' '}
                      keep using it — people in its linked workspaces and on its roster, as
                      membership changes. Untick to choose the audience yourself.
                    </span>
                  </label>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void apply('restricted')}>Restrict</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirming !== null && confirming !== 'restricted'}
        onOpenChange={(o) => !o && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'anyone'
                ? 'Open this arche to anyone?'
                : 'Open this arche to everyone with a Fias account?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  {confirming === 'anyone'
                    ? 'From now on anyone can open it, without signing in.'
                    : 'From now on anyone signed in to Fias can open it.'}
                </p>
                {data.audience === 'restricted' && (
                  <p>
                    It is no longer limited to its team and the people you chose. The people,
                    workspaces, Companies and guilds you added stay listed, but no longer limit who
                    can open it.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) void apply(confirming);
              }}
            >
              Open it up
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/**
 * Save asks first for any change that widens who can use the arche or cannot
 * be undone: restricting, opening a restricted arche up, and opening any arche
 * to people who are not signed in. Narrowing "Anyone" to signed-in users only
 * goes straight through.
 */
function asksFirst(saved: ArcheAudienceKind, next: ArcheAudienceKind): boolean {
  return next === 'restricted' || next === 'anyone' || saved === 'restricted';
}

function granteeLabel(grant: ArcheAudienceGrantDto): string {
  if (grant.granteeType === 'user') {
    return grant.displayName ?? (grant.username ? `@${grant.username}` : grant.granteeId);
  }
  return grant.displayName ?? grant.granteeId;
}

function GrantList({ archeId, grants }: { archeId: string; grants: ArcheAudienceDto['grants'] }) {
  const revoke = useRevokeArcheAudienceGrant(archeId);
  return (
    <ul className="divide-y rounded-md border">
      {grants.map((g) => {
        const name = granteeLabel(g);
        return (
          <li key={g.grantId} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
            <span className="flex items-center gap-2">
              <Badge variant="outline">{GRANTEE_LABEL[g.granteeType]}</Badge>
              <span
                title={g.granteeType === 'user' ? undefined : g.granteeId}
                className={
                  g.displayName || g.granteeType === 'user' ? undefined : 'font-mono text-xs'
                }
              >
                {name}
              </span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={revoke.isPending}
              aria-label={`Remove ${name}’s access`}
              onClick={async () => {
                try {
                  await revoke.mutateAsync(g.grantId);
                } catch (err) {
                  toast({
                    title: 'Could not remove',
                    description: describeCompanyError(err),
                    variant: 'destructive',
                  });
                }
              }}
            >
              Remove
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A workspace, Company or guild can be given access only by someone who
 * belongs to it (the server refuses anything else, GRANTEE_NOT_AFFILIATED),
 * so the choice is a list of the viewer's own — never an id to type. Each list
 * is fetched only once its kind is chosen.
 */
function AddGranteeForm({ archeId }: { archeId: string }) {
  const grant = useGrantArcheAudience(archeId);
  const [type, setType] = useState<GranteeType>('user');
  const [username, setUsername] = useState('');
  const [targetId, setTargetId] = useState('');
  const companies = useMyCompanies({ enabled: type === 'company' });
  const guilds = useQuery({
    queryKey: queryKeys.guilds.my(),
    queryFn: () => api.getMyGuilds(),
    enabled: type === 'guild',
  });
  const memberships = useQuery({
    queryKey: queryKeys.vault.me.memberships(),
    queryFn: () => api.listMyVaultMemberships(),
    enabled: type === 'workspace',
  });
  const workspaces = (memberships.data?.memberships ?? []).filter(
    (m) => m.holderType === 'org' && m.status === 'active',
  );
  const listLoading =
    (type === 'workspace' && memberships.isLoading) ||
    (type === 'company' && companies.isLoading) ||
    (type === 'guild' && guilds.isLoading);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await grant.mutateAsync(
        type === 'user'
          ? { granteeType: 'user', username: username.replace(/^@/, '') }
          : { granteeType: type, granteeId: targetId },
      );
      setUsername('');
      setTargetId('');
      toast({ title: 'Access given' });
    } catch (err) {
      toast({
        title: 'Could not give access',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  const options =
    type === 'workspace'
      ? workspaces.map((w) => ({ id: w.holderId, label: w.holderDisplayName }))
      : type === 'company'
        ? (companies.data ?? []).map((c) => ({ id: c.company.companyId, label: c.company.name }))
        : type === 'guild'
          ? (guilds.data?.guilds ?? []).map((g) => ({ id: g.guildId, label: g.name }))
          : [];

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-wrap items-end gap-2">
      <div className="grid gap-1.5">
        <Label htmlFor={`grantee-type-${archeId}`}>Add</Label>
        <select
          id={`grantee-type-${archeId}`}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={type}
          onChange={(e) => {
            setType(e.target.value as GranteeType);
            setTargetId('');
          }}
        >
          <option value="user">A person</option>
          <option value="workspace">A workspace</option>
          <option value="company">A Company</option>
          <option value="guild">A guild</option>
        </select>
      </div>
      {type === 'user' ? (
        <div className="grid min-w-[14rem] flex-1 gap-1.5">
          <Label htmlFor={`grantee-user-${archeId}`}>Username</Label>
          <UserSearchCombobox
            id={`grantee-user-${archeId}`}
            value={username}
            onValueChange={setUsername}
            onSelectUser={(u) => setUsername(u.username)}
            placeholder="@username"
          />
        </div>
      ) : options.length > 0 ? (
        <div className="grid min-w-[14rem] flex-1 gap-1.5">
          <Label htmlFor={`grantee-target-${archeId}`}>{GRANTEE_LABEL[type]}</Label>
          <select
            id={`grantee-target-${archeId}`}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          >
            <option value="">Choose…</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="min-w-[14rem] flex-1 pb-2 text-xs text-muted-foreground">
          {listLoading ? 'Loading…' : NONE_TO_OFFER[type]}
        </p>
      )}
      <Button type="submit" disabled={grant.isPending || (type === 'user' ? !username : !targetId)}>
        Give access
      </Button>
    </form>
  );
}
